import { FC, useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import css from './appCustom.module.scss';
import { supportLinkLocales } from './const';
import EFVideo from '@/components/UI/EFVideo';
import { useGlobal } from '@/utils/useGlobal';
import NextImage from '@/components/NextImage';
import EFArrowRightBlueSmall from '@/components/UI/svg/Icons/EFArrowRightBlueSmall';

const FEAT_KEY = {
    backup: 'backup',
    smart: 'smart',
    speed: 'speed',
} as const;
const APP_LINK = '/app';

const AppCustom: FC = () => {
    const { useLang, useRouter, Link } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('delta2portablepowerstation.delta2');
    const showAppLink = supportLinkLocales.includes(locale);
    const [winWidth, setWinWidth] = useState(375);
    const [active, setActive] = useState(0);
    const featList = [
        { key: FEAT_KEY.backup, title: t('app_custom_backup_title'), desc: t('app_custom_backup_desc'), img: getLocaleNextImage(locale, FEAT_KEY.backup) },
        { key: FEAT_KEY.smart, title: t('app_custom_smart_title'), desc: t('app_custom_smart_desc'), img: getLocaleNextImage(locale, FEAT_KEY.smart) },
        { key: FEAT_KEY.speed, title: t('app_custom_speed_title'), desc: t('app_custom_speed_desc'), img: getLocaleNextImage(locale, FEAT_KEY.speed) },
    ];

    useEffect(() => {
        window && setWinWidth(window.innerWidth);
    }, []);

    return (
        <div className={css.appCustom}>
            <div className="app-msg-box">
                <div className="app-msg-title">{t('app_custom_title')}</div>
                <div className="app-msg-desc">{t('app_custom_description')}</div>
            </div>
            <div className="app-video-box">
                <EFVideo className="video" sources={getVideoSource(locale)} />
                <div className="video-content">
                    <p className="video-desc">{t('app_custom_video_desc')}</p>
                    {showAppLink && (
                        <Link href={APP_LINK} className="learn-more-wrapper" newWin>
                            <div>{t('learn_more')}</div>
                            <EFArrowRightBlueSmall />
                        </Link>
                    )}
                </div>
            </div>
            <Swiper
                className="feat-box"
                loop
                initialSlide={0}
                preloadImages={false}
                lazy={true}
                centeredSlides={true}
                spaceBetween={(winWidth / 375) * 25}
                slidesPerView={'auto'}
                grabCursor={true}
                navigation={true}
                onSlideChange={(swiper: { activeIndex: number }) => setActive(swiper.activeIndex % featList.length)}
            >
                {featList?.map((featItem) => (
                    <SwiperSlide key={featItem.key} className="feat-box-item">
                        <div className="feat-box-img">{featItem.img}</div>
                        <div className="feat-box-title">{featItem.title}</div>
                        <div className="feat-box-desc">{featItem.desc}</div>
                    </SwiperSlide>
                ))}
            </Swiper>
            <div className="feat-pagination">
                {featList.map((featItem, idx) => (
                    <div className={`feat-pagination-item ${active === idx ? 'feat-pagination-item-active' : ''}`} key={`pagination-${featItem.key}`}></div>
                ))}
            </div>
        </div>
    );
};

const getLocaleNextImage = (locale: string, key: keyof typeof FEAT_KEY) => {
    let webpSrc = '';
    let src = '';

    switch (locale) {
        case 'jp': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-a-jp.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-b-jp.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-jp.webp');
                    src = require('@publicLocal/delta2/common/feat-c-jp.png');
            }
            break;
        }
        case 'fr': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-a-fr.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-b-fr.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-fr.webp');
                    src = require('@publicLocal/delta2/common/feat-c-fr.png');
            }
            break;
        }

        case 'de': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-de.webp');
                    src = require('@publicLocal/delta2/common/feat-a-de.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-de.webp');
                    src = require('@publicLocal/delta2/common/feat-b-de.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-de.webp');
                    src = require('@publicLocal/delta2/common/feat-c-de.png');
            }
            break;
        }
        case 'es': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-es.webp');
                    src = require('@publicLocal/delta2/common/feat-a-es.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-es.webp');
                    src = require('@publicLocal/delta2/common/feat-b-es.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-es.webp');
                    src = require('@publicLocal/delta2/common/feat-c-es.png');
            }
            break;
        }
        case 'it': {
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-it.webp');
                    src = require('@publicLocal/delta2/common/feat-a-it.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-it.webp');
                    src = require('@publicLocal/delta2/common/feat-b-it.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-it.webp');
                    src = require('@publicLocal/delta2/common/feat-c-it.png');
            }
            break;
        }
        default:
            switch (key) {
                case FEAT_KEY.backup:
                    webpSrc = require('@publicLocal/delta2/common/feat-a-en.webp');
                    src = require('@publicLocal/delta2/common/feat-a-en.png');
                    break;

                case FEAT_KEY.smart:
                    webpSrc = require('@publicLocal/delta2/common/feat-b-en.webp');
                    src = require('@publicLocal/delta2/common/feat-b-en.png');
                    break;

                default:
                    webpSrc = require('@publicLocal/delta2/common/feat-c-en.webp');
                    src = require('@publicLocal/delta2/common/feat-c-en.png');
            }
    }

    return <NextImage webpSrc={webpSrc} src={src} alt={FEAT_KEY[key]} />;
};

const getVideoSource = (locale: string) => {
    const arr = ['fr', 'de', 'it', 'es', 'jp'];
    let imgLoc = 'en';
    if (arr.includes(locale)) {
        imgLoc = locale;
    }
    return [`/media/delta2/h5/app-custom/animk-${imgLoc}.webm`, `/media/delta2/h5/app-custom/animk-${imgLoc}.mp4`];
};

export default AppCustom;
